
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';

/**
 * Hook for checking user roles via RPC functions
 * More secure than client-side role checks
 */
export function useRoleCheck() {
  const { isAuthenticated, user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Check roles using user role
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      // Get the user's role
      const userRole = user.role?.toUpperCase() || 'USER';
      
      // Check admin and super admin status
      const adminStatus = checkRoleAccess(userRole, 'ADMIN');
      const superAdminStatus = checkRoleAccess(userRole, 'SUPER_ADMIN');
      
      setIsAdmin(adminStatus);
      setIsSuperAdmin(superAdminStatus);
    } catch (error) {
      console.error("[useRoleCheck] Error checking roles:", error);
      // Fallback to client-side role check if RPC fails
      const normalizedRole = user.role?.toUpperCase() || 'USER';
      setIsAdmin(normalizedRole === 'ADMIN' || normalizedRole === 'SUPER_ADMIN');
      setIsSuperAdmin(normalizedRole === 'SUPER_ADMIN');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);
  
  /**
   * Check if a role has access to a required role
   * @param userRole The user's role
   * @param requiredRole The role required for access
   * @returns true if the user has access to the required role
   */
  const checkRoleAccess = (userRole: string, requiredRole: string): boolean => {
    const roleHierarchy = {
      'USER': 0,
      'DEALER': 1,
      'ADMIN': 2,
      'SUPER_ADMIN': 3
    };
    
    const userRoleLevel = roleHierarchy[userRole as keyof typeof roleHierarchy] || 0;
    const requiredRoleLevel = roleHierarchy[requiredRole as keyof typeof roleHierarchy] || 0;
    
    return userRoleLevel >= requiredRoleLevel;
  };
  
  /**
   * Check if current user has access to a specific role
   * @param requiredRole The role required for access
   * @returns true if user has the required role
   */
  const hasRole = useCallback((requiredRole: string): boolean => {
    if (!user || !isAuthenticated) return false;
    
    const normalizedRequiredRole = requiredRole.toUpperCase();
    const normalizedUserRole = user.role?.toUpperCase() || 'USER';
    
    if (normalizedRequiredRole === 'SUPER_ADMIN') {
      return isSuperAdmin;
    }
    
    if (normalizedRequiredRole === 'ADMIN') {
      return isAdmin;
    }
    
    return checkRoleAccess(normalizedUserRole, normalizedRequiredRole);
  }, [isAuthenticated, user, isAdmin, isSuperAdmin]);
  
  return {
    isAdmin,
    isSuperAdmin,
    loading,
    hasRole
  };
}
